import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Dropdown} from '@/components/atoms';
import {
  PageHeader,
  TargetTemplateUpload,
  TextInputField
} from '@/components/molecules';
import {PageContainer} from '@/components/organisms';
import {useDropdown} from '@/hooks/Dropdown-hook';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  perfectStoreInitialFormData,
  resetPerfectStoreFormData,
  setPerfectStoreFormData
} from '@/store/slices/perfectStoreSlice';
import {FONT_SIZE} from '@/utils/constants/globalStyle';
import {Constants} from '@/utils/constants/commonConstants';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import {usePerfectStore} from '@/hooks/usePerfectStore';
import {
  FIRST_POSITION_CSV_RULES,
  FIRST_POSITION_TEMPLATE_CSV,
  isFirstPositionTarget
} from '@/utils/constants/perfectStoreConstants';
import AvailabilitySection from './AvailabilitySection';
import AdditionalOptionsSection from './AdditionalOptionsSection';
import {targetNameUiSchema} from './schemas';

const PerfectStoreSetUp = ({formData, setFormData}) => {
  const dispatch = useAppDispatch();
  const perfectStoreId = useAppSelector((state) => state?.perfectStore?.id);
  const {onTemplateUpdate} = usePerfectStore();
  const {dropdownList: targetTypeOptions} = useDropdown({
    dropdownType: DROPDOWN_TYPES.PerfectStoreTargetType
  });

  const hasTargetType = !!formData?.targetType;
  const showFirstPositionSetup = isFirstPositionTarget(formData?.targetType);

  const targetTypeDropdownValue = useMemo(() => {
    const current = formData?.targetType;
    if (!current) return [];
    const match = (targetTypeOptions || []).find(
      (opt) => opt.value === current || opt.displayText === current
    );
    return match
      ? [match]
      : [{id: current, displayText: current, value: current}];
  }, [formData?.targetType, targetTypeOptions]);

  const handleFilterData = useCallback(
    (data) => {
      setFormData((prev) => {
        const next = {...prev, ...data};
        dispatch(setPerfectStoreFormData(next));
        return next;
      });
    },
    [dispatch, setFormData]
  );

  const handleResetFormData = useCallback(
    (data) => {
      const defaultFormData = {
        ...perfectStoreInitialFormData,
        targetType: data?.targetType || ''
      };
      setFormData(defaultFormData);
      dispatch(resetPerfectStoreFormData(defaultFormData));
    },
    [dispatch, setFormData]
  );

  return (
    <>
      <PageContainer data-testid='perfect-store-setup-step'>
        <PageHeader fontSize={FONT_SIZE.LG} title='Perfect Store Setup' />
        <div className='flex flex-row flex-wrap items-start gap-6 pb-4'>
          <div>
            <Dropdown
              key='perfect-store-target-type'
              label='Target Type *'
              value={targetTypeDropdownValue}
              updateValue={(val) => {
                const selected = val?.[0];
                handleResetFormData({
                  targetType: selected?.value ?? ''
                });
              }}
              isJsonWrap={false}
              childList={targetTypeOptions}
              className='dropdown-width text-sm'
              isDark={true}
              placeholder='Target Type'
              isClearable={true}
              selection='single'
              size='medium'
            />
          </div>
          {hasTargetType && (
            <div className='max-w-[296px]'>
              <TextInputField
                value={formData?.name}
                updateValue={(val, isValid) => {
                  handleFilterData({
                    name: val,
                    nameValid: isValid
                  });
                }}
                label='Name *'
                schema={{type: 'string'}}
                uiSchema={targetNameUiSchema}
                visible={true}
              />
            </div>
          )}
        </div>

        {showFirstPositionSetup && (
          <>
            <p className='mb-4 mt-2 text-lg font-bold'>
              First Position Store Target
            </p>
            <TargetTemplateUpload
              formData={formData}
              setFormData={setFormData}
              templateCsv={FIRST_POSITION_TEMPLATE_CSV}
              downloadFileNamePrefix='FirstPositionTarget'
              uploadModalTitle='Upload Store Target List'
              uploadModalSubTitle='Upload Store Target file'
              isCsvValidationRequired={true}
              customRules={FIRST_POSITION_CSV_RULES}
              uploadButtonLabel='Upload Template'
              uploadFor={Constants.PerfectStore}
              onTemplateUpdate={perfectStoreId ? onTemplateUpdate : undefined}
              salesTargetId={perfectStoreId ?? undefined}
            />
          </>
        )}
      </PageContainer>

      {showFirstPositionSetup && (
        <PageContainer data-testid='perfect-store-availability-card'>
          <PageHeader fontSize={FONT_SIZE.LG} title='Availability' />
          <AvailabilitySection
            formData={formData}
            onChange={handleFilterData}
          />
          <hr className='my-6 border-gray' />
          <p className='mb-4 text-lg font-bold'>Additional Options</p>
          <AdditionalOptionsSection
            formData={formData}
            onChange={handleFilterData}
          />
        </PageContainer>
      )}
    </>
  );
};

PerfectStoreSetUp.propTypes = {
  formData: PropTypes.object,
  setFormData: PropTypes.func.isRequired
};

export default PerfectStoreSetUp;
